import { gql, useQuery } from "@apollo/client";
import _ from "lodash";
import { DateTime } from "luxon";
import MUIDataTable, {
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  MUISortOptions
} from "mui-datatables";
import { useEffect, useMemo, useState } from "react";

import { makeStyles, Paper } from "@material-ui/core";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import { useSubaccount } from "src/components/SubaccountProvider";
import NumberStat from "src/components/stats/NumberStat";
import {
  getCallerStats_campaign_stats_callers,
  getCallerStats,
  getCallerStatsVariables
} from "src/graphql/generated/getCallerStats";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import { CallerStatus } from "src/graphql/generated/globalTypes";
import formatDuration from "src/util/formatDuration";
import formatPhoneNumber from "src/util/formatPhoneNumber";

type Caller = getCallerStats_campaign_stats_callers & {
  statusLabel: string;
  averageCallDuration: number;
  averageWaitTime: number;
};

const useStyles = makeStyles(theme => ({
  secondCellRow: {
    fontStyle: "italic"
  },
  paper: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
    maxWidth: "100%"
  },
  numberStatRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    flexWrap: "wrap"
  }
}));

const GET_CALLER_STATS = gql`
  query getCallerStats($campaignId: ID!) {
    campaign(id: $campaignId) {
      id
      stats {
        callers {
          phone_number
          name
          email
          status
          lastUpdate
          calledToday
          secondsInCall
          secondsInCallToday
          callsAnswered
          callsAnsweredToday
          secondsWaiting
          secondsWaitingToday
          timesInQueue
          timesInQueueToday
          totalTimeLoggedIn
          totalTimeLoggedInToday
        }
      }
    }
  }
`;

const STATUS_LABELS: { [status in CallerStatus]: string } = {
  [CallerStatus.COMPLETE]: "Signed Off",
  [CallerStatus.IN_QUEUE]: "Waiting For Call",
  [CallerStatus.CONNECTED]: "Connecting",
  [CallerStatus.IN_SURVEY]: "Completing Survey",
  [CallerStatus.IN_CALL]: "In A Call"
};

function formatRelativeDate(dateMillis: number): string {
  return DateTime.fromMillis(dateMillis).toRelative() || "";
}

function StatusCell({ caller }: { caller: Caller }): JSX.Element {
  const [lastUpdateStr, setLastUpdateStr] = useState<string>(
    formatRelativeDate(caller.lastUpdate)
  );
  useEffect(() => {
    const intervalHandle = setInterval(() => {
      setLastUpdateStr(formatRelativeDate(caller.lastUpdate));
    }, 1000);

    return () => clearTimeout(intervalHandle);
  }, [caller.lastUpdate]);

  const classes = useStyles();

  return (
    <>
      <span>{caller.statusLabel}</span>
      <br />
      <span className={classes.secondCellRow}>Since: {lastUpdateStr}</span>
    </>
  );
}

function CallsAnsweredCell({ caller }: { caller: Caller }): JSX.Element {
  const classes = useStyles();

  return (
    <>
      <span>{caller.callsAnswered}</span>
      {caller.callsAnsweredToday ? (
        <>
          <br />
          <span className={classes.secondCellRow}>
            Today: {caller.callsAnsweredToday}
          </span>
        </>
      ) : null}
    </>
  );
}

function AverageCallDurationCell({ caller }: { caller: Caller }): JSX.Element {
  const classes = useStyles();

  return (
    <>
      <span>{formatDuration(Math.round(caller.averageCallDuration))}</span>
      {caller.secondsInCallToday && caller.callsAnsweredToday ? (
        <>
          <br />
          <span className={classes.secondCellRow}>
            Today:{" "}
            {formatDuration(
              Math.round(caller.secondsInCallToday / caller.callsAnsweredToday)
            )}
          </span>
        </>
      ) : null}
    </>
  );
}

function AverageWaitTimeCell({ caller }: { caller: Caller }): JSX.Element {
  const classes = useStyles();

  return (
    <>
      <span>{formatDuration(Math.round(caller.averageWaitTime))}</span>
      {caller.secondsWaitingToday && caller.timesInQueueToday ? (
        <>
          <br />
          <span className={classes.secondCellRow}>
            Today:{" "}
            {formatDuration(
              Math.round(caller.secondsWaitingToday / caller.timesInQueueToday)
            )}
          </span>
        </>
      ) : null}
    </>
  );
}

function TotalTimeCell({
  totalDuration,
  totalDurationToday
}: {
  totalDuration: number;
  totalDurationToday: number | null;
}): JSX.Element {
  const classes = useStyles();

  return (
    <>
      <span>{formatDuration(totalDuration)}</span>
      {totalDurationToday ? (
        <>
          <br />
          <span className={classes.secondCellRow}>
            Today: {formatDuration(totalDurationToday)}
          </span>
        </>
      ) : null}
    </>
  );
}

function CallerStatusSummary({ callers }: { callers: Caller[] }): JSX.Element {
  const classes = useStyles();
  const counts = _.countBy(callers, "status");

  return (
    <Paper className={classes.paper}>
      <div className={classes.numberStatRow}>
        {[
          CallerStatus.IN_CALL,
          CallerStatus.IN_QUEUE,
          CallerStatus.CONNECTED,
          CallerStatus.IN_SURVEY,
          CallerStatus.COMPLETE
        ].map(status => (
          <NumberStat key={status} value={counts[status] || 0}>
            {STATUS_LABELS[status]}
          </NumberStat>
        ))}
      </div>
    </Paper>
  );
}

export default function Callers({
  campaign
}: {
  campaign: getCampaign_campaign;
}): JSX.Element {
  const { subaccount } = useSubaccount()!;

  const { loading, error, data } = useQuery<
    getCallerStats,
    getCallerStatsVariables
  >(GET_CALLER_STATS, {
    variables: { campaignId: campaign.id },
    pollInterval: 30 * 1000
  });

  const callers: Caller[] = useMemo(() => {
    if (!data?.campaign?.stats?.callers) {
      return [];
    }

    return data.campaign.stats.callers.map(caller => ({
      ...caller,
      phone_number: formatPhoneNumber(caller.phone_number),
      name: caller.name || "(none given)",
      email: caller.email || "(none given)",
      statusLabel: STATUS_LABELS[caller.status],
      averageCallDuration: caller.callsAnswered
        ? caller.secondsInCall / caller.callsAnswered
        : 0,
      averageWaitTime: caller.timesInQueue
        ? caller.secondsWaiting / caller.timesInQueue
        : 0
    }));
  }, [data]);

  const [sortOrder, setSortOrder] = useState<MUISortOptions>({
    name: "phone_number",
    direction: "asc"
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  if (!callers) {
    return <div />;
  }

  if (!callers.length) {
    return (
      <div>
        <h2>Callers</h2>
        <p>No Callers Yet</p>
      </div>
    );
  }

  const columns: MUIDataTableColumnDef[] = [
    {
      name: "phone_number",
      label: "Phone Number",
      options: {
        filter: false
      }
    },
    {
      name: "name",
      label: "Name",
      options: {
        filter: false
      }
    },
    ...(subaccount.settings.collectCallerEmail
      ? [
          {
            name: "email",
            label: "Email",
            options: {
              filter: false,
              display: false
            }
          }
        ]
      : []),
    {
      name: "statusLabel",
      label: "Status",
      options: {
        customBodyRenderLite: dataIndex => (
          <StatusCell caller={callers[dataIndex]} />
        )
      }
    },
    {
      name: "callsAnswered",
      label: "Calls Answered",
      options: {
        filter: false,
        customBodyRenderLite: dataIndex => (
          <CallsAnsweredCell caller={callers[dataIndex]} />
        )
      }
    },
    {
      name: "averageCallDuration",
      label: "Average Call Duration",
      options: {
        filter: false,
        customBodyRenderLite: dataIndex => (
          <AverageCallDurationCell caller={callers[dataIndex]} />
        )
      }
    },
    {
      name: "averageWaitTime",
      label: "Average Wait Time",
      options: {
        filter: false,
        customBodyRenderLite: dataIndex => (
          <AverageWaitTimeCell caller={callers[dataIndex]} />
        )
      }
    },
    {
      name: "totalTimeLoggedIn",
      label: "Total Time Logged In",
      options: {
        filter: false,
        customBodyRenderLite: dataIndex => (
          <TotalTimeCell
            totalDuration={callers[dataIndex].totalTimeLoggedIn}
            totalDurationToday={callers[dataIndex].totalTimeLoggedInToday}
          />
        )
      }
    },
    {
      name: "secondsInCall",
      label: "Total Time In Calls",
      options: {
        filter: false,
        customBodyRenderLite: dataIndex => (
          <TotalTimeCell
            totalDuration={callers[dataIndex].secondsInCall}
            totalDurationToday={callers[dataIndex].secondsInCallToday}
          />
        )
      }
    }
  ];

  const options: MUIDataTableOptions = {
    filterType: "checkbox",
    elevation: 0,
    print: false,
    selectableRows: "none",
    sortOrder,
    onColumnSortChange: (name: string, direction: "asc" | "desc") =>
      setSortOrder({ name, direction })
  };

  return (
    <>
      <h2>Callers</h2>
      <CallerStatusSummary callers={callers} />
      <MUIDataTable
        title=""
        data={callers}
        columns={columns}
        options={options}
      />
    </>
  );
}
